import React, { useEffect, useState } from 'react';
import { graphql, Script } from 'gatsby';
import PropTypes from 'prop-types';
import querystring from 'query-string';
import { Box } from '@chakra-ui/react';

import { ProductContainerProvider } from '@rsc/contexts';

import Layout from '@components/layout';
import ProductContainer from '@rsc/ui-components/tailwind/ProductContainer';
import { siteSettingsShape } from '@rsc/ui-components/dept/types';

import {
  createOpenGraphTags,
  filteredVariants,
  formatReviewSku,
  getProductSchema,
  variantOptionsFilter,
} from '@rsc/utils';
import { usePetsafeInternationalContext } from '@util/static-translations';
import { getLocalizedPagePath } from '@rsc/gatsby-localization-helpers';
import { showBazaarVoice } from '@util/helpers';

function ProductDetailTemplate({ data, location, pageContext }) {
  const { linkHreflangValues } = pageContext;
  const { state, search, pathname } = location;
  const query = querystring.parse(search);
  const { SEOMetadata, store, shopifyProduct } = data.product;
  const [activeSku, setActiveSku] = useState(null);
  const [productSchemaData, setProductSchemaData] = useState(null);
  const { tt, locale } = usePetsafeInternationalContext();

  if (state?.searchedProduct) {
    shopifyProduct.searchedProduct = state.searchedProduct;
  }

  if (shopifyProduct?.variants.length > 1) {
    /* eslint-disable no-param-reassign */
    const filtered = filteredVariants(shopifyProduct?.variants);
    const options = variantOptionsFilter(filtered);

    data.product.shopifyProduct.variants = filtered;
    data.product.shopifyProduct.options = options;
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && !pathname.startsWith('/preview/')) {
      setActiveSku(query?.sku || '');
    }

    if (
      typeof window !== 'undefined' &&
      shopifyProduct?.variants?.length !== 0
    ) {
      setActiveSku(query?.sku || shopifyProduct?.variants[0].sku);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOptionChange = ({ variant }) => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location);
      if (variant) {
        url.searchParams.set('sku', variant.sku);
        setActiveSku(variant.sku);
      } else {
        url.searchParams.delete('sku');
        setActiveSku(null);
      }
      window.history.replaceState(null, '', url.toString());
    }
  };

  // Watch active sku parameter and update shopifyProduct schema data if sku changes
  useEffect(() => {
    setProductSchemaData(
      JSON.stringify(getProductSchema(shopifyProduct, activeSku))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSku]);

  const currentPrice =
    shopifyProduct?.variants?.find(variant => variant.sku === activeSku)
      ?.price ||
    shopifyProduct?.variants?.[0] ||
    shopifyProduct?.priceRangeV2?.minVariantPrice?.amount;

  return (
    <>
      <Layout
        headTagData={{
          title:
            SEOMetadata?.title ||
            tt(s => s.commonLabels.petsafeTitleLabel, {
              title: shopifyProduct?.title,
            }),
          description:
            SEOMetadata?.description ||
            tt(s => s.productDetailPage.headDescriptionLabel, {
              title: store.title,
            }) ||
            shopifyProduct?.description,
          canonicalUrl: pageContext.isNonCommerce
            ? `https://www.petsafe.com/product/${shopifyProduct?.handle}`
            : SEOMetadata?.canonicalUrl,
          noIndex: pageContext.isNonCommerce ? true : SEOMetadata?.noIndex,
          noFollow: pageContext.isNonCommerce ? true : SEOMetadata?.noFollow,
          includeBVScript: false,
          enableKlaviyo: true,
          meta: createOpenGraphTags(
            {
              title:
                SEOMetadata?.ogTitle ||
                SEOMetadata?.title ||
                tt(s => s.commonLabels.petsafeTitleLabel, {
                  title: shopifyProduct?.title,
                }),
              description:
                SEOMetadata?.ogDescription ||
                SEOMetadata?.description ||
                tt(s => s.productDetailPage.headDescriptionLabel, {
                  title: store.title,
                }) ||
                shopifyProduct?.description,
              url: getLocalizedPagePath(
                SEOMetadata?.ogUrl || SEOMetadata?.canonicalUrl || pathname,
                locale
              ),
              type: 'product',
              'og:price:currency': 'USD',
              'og:price:amount': currentPrice,
              image: SEOMetadata?.ogImage || store?.previewImageUrl,
            },
            'petsafe'
          ),
        }}
        scriptsData={[
          <script type="application/ld+json" key="pdp-schema">
            {productSchemaData}
          </script>,
        ]}
        isPDP
        isNonCommerce={pageContext.isNonCommerce}
        linkHreflangValues={linkHreflangValues}
      >
        <ProductContainerProvider
          product={data.product}
          location={location}
          activeSku={activeSku}
          onOptionChange={onOptionChange}
        >
          <ProductContainer
            data={data}
            location={location}
            pageContext={pageContext}
          />
        </ProductContainerProvider>
        {showBazaarVoice(locale) && (
          <Script
            defer
            fetchPriority="low"
            src={`https://apps.bazaarvoice.com/deployments/petsafe/main_site/${process.env.GATSBY_BAZAARVOICE_ENVIRONMENT}/en_US/bv.js`}
            onLoad={() => {
              window?.$BV?.configure('global', {
                productId: `${formatReviewSku(
                  shopifyProduct?.variants?.[0]?.sku
                )}`,
              });

              window?.$BV?.ui('rr', 'show_reviews', {
                doShowContent() {},
              });

              window?.$BV?.ui('qa', 'show_questions', {
                doShowContent() {},
              });
            }}
          />
        )}
      </Layout>
      <Box h={[14, null, 0]} background="primary.base" />
    </>
  );
}

export const query = graphql`
  fragment SanityGlobalProductFields on SanityGlobalProduct {
    _id
    badge {
      hex
      text
    }
    dynamicFeatures {
      text
    }
    store {
      previewImageUrl
      title
      slug {
        current
      }
      metafields {
        name
        value
      }
      variants {
        metafields {
          name
          value
        }
        sku
      }
    }
    shopifyProduct {
      shopifyId: gid
      title
      handle
      productType
      description
      descriptionHtml
      media {
        ... on SanityProductMediaImage {
          id
          image {
            altText
            src
          }
        }
      }
      publishedAt
      productLifecycle
      vendor
      tags: tagsV2
      priceRangeV2 {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      options {
        name
        values
      }
      variants {
        title
        shopifyId: gid
        availableForSale
        sku
        selectedOptions {
          name
          value
        }
        price
        compareAtPrice
        media {
          ... on SanityProductMediaImage {
            id
            image {
              src
              altText
            }
          }
        }
        variantLifecycle
        language
      }
    }
  }

  query ProductPageQuery($_id: String = "", $locale: String) {
    product: sanityGlobalProduct(
      _id: { eq: $_id }
      siteIdentifier: { eq: "petsafe" }
    ) {
      descriptionHeading
      displaySizingChart
      dynamicFeatures {
        text
      }
      pdpAttributePairs {
        _key
        name
        value
      }
      badge {
        hex
        text
      }
      productAddOns {
        heading
        products {
          _key
          product {
            SEOMetadata {
              title
            }
            store {
              title
              previewImageUrl
              slug {
                current
              }
            }
            shopifyProduct {
              shopifyId: gid
              title
              vendor
              productType
              variants {
                shopifyId: gid
                sku
                title
                selectedOptions {
                  name
                  value
                }
                price
                compareAtPrice
                media {
                  id
                  ... on SanityProductMediaImage {
                    id
                    image {
                      src
                      altText
                    }
                  }
                }
              }
            }
          }
          variant {
            sku
          }
        }
      }
      productBlocks {
        ...DeptBlockFields
      }
      SEOMetadata {
        title
        description
        canonicalUrl
        noIndex
        noFollow
        ogTitle
        ogDescription
        ogUrl
        ogImage {
          asset {
            _id
            url
          }
        }
      }
      sizingChartUrl
      store {
        title
        description
        metafields {
          name
          value
        }
        previewImageUrl
        variants {
          metafields {
            name
            value
          }
          sku
        }
      }
      manuals {
        asset: _rawAsset(resolveReferences: { maxDepth: 3 })
        label
      }
      warnings {
        _key
        warning: localizedWarningValue {
          identifier
          block: _rawBlock(resolveReferences: { maxDepth: 10 })
        }
      }
      warranties: _rawWarranties(resolveReferences: { maxDepth: 10 })
      breadcrumbs {
        linkType
        href
        internalLink: localizedInternalLinkValue {
          ...internalLinkFields
        }
        blank
        caption
      }
      supportPage
      ...SanityGlobalProductFields
    }
    siteSettings: sanitySiteSettings(
      siteIdentifier: { eq: "petsafe" }
      language: { eq: $locale }
    ) {
      ...SiteSettingsFields
    }
  }
`;

const MediaShape = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string,
    image: PropTypes.shape({
      src: PropTypes.string,
      alt: PropTypes.string,
    }),
  })
);

ProductDetailTemplate.propTypes = {
  pageContext: PropTypes.shape({
    isNonCommerce: PropTypes.bool,
    linkHreflangValues: {},
  }).isRequired,
  data: PropTypes.shape({
    product: PropTypes.shape({
      SEOMetadata: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        canonicalUrl: PropTypes.string,
        noIndex: PropTypes.bool,
        noFollow: PropTypes.bool,
        ogTitle: PropTypes.string,
        ogDescription: PropTypes.string,
        ogUrl: PropTypes.string,
        ogImage: PropTypes.string,
      }),
      store: PropTypes.shape({
        description: PropTypes.string,
        title: PropTypes.string,
        previewImageUrl: PropTypes.string,
      }),
      manuals: PropTypes.arrayOf(PropTypes.shape({})),
      supportPage: PropTypes.string,
      shopifyProduct: PropTypes.shape({
        handle: PropTypes.string,
        description: PropTypes.string,
        descriptionHtml: PropTypes.string,
        searchedProduct: PropTypes.shape({}),
        title: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.shape({})),
        variants: PropTypes.arrayOf(
          PropTypes.shape({
            sku: PropTypes.string,
            price: PropTypes.number,
            media: MediaShape,
          })
        ),
        priceRangeV2: PropTypes.shape({
          minVariantPrice: PropTypes.shape({
            amount: PropTypes.string,
            currencyCode: PropTypes.string,
          }),
          maxVariantPrice: PropTypes.shape({
            amount: PropTypes.string,
            currencyCode: PropTypes.string,
          }),
        }),
        media: MediaShape,
      }),
      productBlocks: PropTypes.shape({
        blocks: PropTypes.arrayOf(PropTypes.shape({})),
      }),
      dynamicFeatures: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    siteSettings: siteSettingsShape.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      searchedProduct: PropTypes.shape({}),
    }),
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
};

export default ProductDetailTemplate;
